import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  AccountBalance,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  People,
  ShoppingCart,
  LocalPharmacy,
  Refresh,
  ExpandMore,
  ExpandLess,
  Business,
  Assessment,
  MonetizationOn,
  CreditCard,
  Inventory,
  Receipt,
  AccountBalanceWallet,
  TrendingFlat,
  PieChart,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, ComposedChart } from 'recharts';
import dayjs from 'dayjs';
import CashFlowDiagram from './CashFlowDiagram';
import { Store } from '../types';
import { getAuthHeaders } from '../services/api';

interface KPIData {
  sales: {
    total_bills: number;
    unique_customers: number;
    unique_medicines: number;
    total_sales: number;
    total_mrp: number;
    total_landing_cost: number;
    total_gst: number;
    total_quantity: number;
    avg_order_value: number;
  };
  purchase: {
    total_bills: number;
    unique_distributors: number;
    total_amount: number;
    total_discount: number;
    total_ptr: number;
  };
  financial: {
    gross_profit: number;
    net_profit: number;
    profit_margin: number;
    total_expenses: number;
    bank_credits: number;
    bank_debits: number;
    avg_cash_position: number;
    avg_bank_balance: number;
  };
  customers: {
    new_customers: number;
  };
  period: {
    start_date: string;
    end_date: string;
  };
}

interface StaffPerformance {
  staff_name: string;
  total_bills: number;
  unique_customers_served: number;
  unique_medicines_sold: number;
  total_sales: number;
  total_mrp: number;
  avg_bill_value: number;
  total_items_sold: number;
}

interface Distributor {
  distributor_name: string;
  total_bills: number;
  total_invoice_amount: number;
  total_discount: number;
  total_ptr: number;
  discount_percentage: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const FinanceDashboard: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('all');
  const [dateRange, setDateRange] = useState({
    start: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    end: dayjs().format('YYYY-MM-DD')
  });
  const [kpiData, setKpiData] = useState<KPIData | null>(null);
  const [staffData, setStaffData] = useState<StaffPerformance[]>([]);
  const [distributorData, setDistributorData] = useState<Distributor[]>([]);
  const [trendsData, setTrendsData] = useState<any[]>([]);
  const [purchaseTrendsData, setPurchaseTrendsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState({
    staff: true,
    distributors: true,
    trends: true
  });

  // Fetch stores on component mount
  useEffect(() => {
    fetchStores();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    fetchFinanceData();
  }, [selectedStore, dateRange]);

  const fetchStores = async () => {
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch('/api/lookup/stores', {
        headers: {
          ...authHeaders
        }
      });
      
      if (response.ok) {
        const stores = await response.json();
        console.log('Stores loaded:', stores);
        setStores(stores);
      } else {
        console.error('Failed to fetch stores:', response.status);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchFinanceData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        store_id: selectedStore,
        date_from: dateRange.start,
        date_to: dateRange.end
      });

      console.log('Fetching finance data with params:', {
        store_id: selectedStore,
        date_from: dateRange.start,
        date_to: dateRange.end
      });

      // Fetch all data in parallel
      const authHeaders = await getAuthHeaders();
      const [kpiResponse, staffResponse, distributorResponse, trendsResponse, purchaseTrendsResponse] = await Promise.all([
        fetch(`/api/finance/kpi-summary?${params}`, {
          headers: {
            ...authHeaders
          }
        }),
        fetch(`/api/finance/staff-performance?${params}`, {
          headers: {
            ...authHeaders
          }
        }),
        fetch(`/api/finance/top-distributors?${params}`, {
          headers: {
            ...authHeaders
          }
        }),
        fetch(`/api/finance/sales-trends?${params}&group_by=day`, {
          headers: {
            ...authHeaders
          }
        }),
        fetch(`/api/finance/purchase-trends?${params}&group_by=day`, {
          headers: {
            ...authHeaders
          }
        })
      ]);

      const [kpiResult, staffResult, distributorResult, trendsResult, purchaseTrendsResult] = await Promise.all([
        kpiResponse.json(),
        staffResponse.json(),
        distributorResponse.json(),
        trendsResponse.json(),
        purchaseTrendsResponse.json()
      ]);

      if (kpiResult.success) setKpiData(kpiResult.data);
      if (staffResult.success) setStaffData(staffResult.data);
      if (distributorResult.success) setDistributorData(distributorResult.data);
      if (trendsResult.success) setTrendsData(trendsResult.data);
      if (purchaseTrendsResult.success) setPurchaseTrendsData(purchaseTrendsResult.data);

    } catch (error) {
      console.error('Error fetching finance data:', error);
      setError('Failed to fetch finance data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev: typeof expandedSections) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading && !kpiData) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Finance Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Comprehensive financial analysis and KPI tracking
        </Typography>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Store</InputLabel>
              <Select
                value={selectedStore}
                label="Store"
                onChange={(e: any) => setSelectedStore(e.target.value)}
              >
                <MenuItem value="all">All Stores</MenuItem>
                {stores.map((store: any) => (
                  <MenuItem key={store.id} value={store.id.toString()}>
                    {store.store_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={dateRange.start}
              onChange={(e: any) => setDateRange((prev: any) => ({ ...prev, start: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={dateRange.end}
              onChange={(e: any) => setDateRange((prev: any) => ({ ...prev, end: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchFinanceData}
              disabled={loading}
              sx={{ height: '56px' }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
      </Box>

      {kpiData && (
        <>
          {/* Main Financial KPIs - Top Row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Total Income (Sales) */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AttachMoney sx={{ mr: 1, color: 'white' }} />
                    <Typography variant="h6" sx={{ color: 'white' }}>Total Income</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {formatCurrency(kpiData.sales.total_sales)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {formatNumber(kpiData.sales.total_bills)} bills
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Expenses */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CreditCard sx={{ mr: 1, color: 'white' }} />
                    <Typography variant="h6" sx={{ color: 'white' }}>Total Expenses</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {formatCurrency(kpiData.financial.total_expenses)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Operating costs
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Accounts Receivable (Sales Outstanding) */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccountBalance sx={{ mr: 1, color: 'white' }} />
                    <Typography variant="h6" sx={{ color: 'white' }}>Accounts Receivable</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {formatCurrency(kpiData.sales.total_sales - kpiData.sales.total_landing_cost)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Outstanding sales
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Accounts Payable (Purchase Outstanding) */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Receipt sx={{ mr: 1, color: 'white' }} />
                    <Typography variant="h6" sx={{ color: 'white' }}>Accounts Payable</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {formatCurrency(kpiData.purchase.total_amount)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Purchase outstanding
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Financial Health & Performance Row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Gross Profit */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUp color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Gross Profit</Typography>
                  </Box>
                  <Typography variant="h4" color={kpiData.financial.gross_profit >= 0 ? "primary.main" : "error.main"}>
                    {formatCurrency(kpiData.financial.gross_profit)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sales - Cost of Goods
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Net Profit */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUp color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6">Net Profit</Typography>
                  </Box>
                  <Typography variant="h4" color={kpiData.financial.net_profit >= 0 ? "success.main" : "error.main"}>
                    {formatCurrency(kpiData.financial.net_profit)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {kpiData.financial.profit_margin}% margin
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Cash at End of Month */}
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccountBalanceWallet color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6">Cash at End of Month</Typography>
                  </Box>
                  <Typography variant="h4" color="info.main">
                    {formatCurrency(kpiData.financial.avg_cash_position)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average cash position
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Net Profit Margin % - Donut Chart */}
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CardContent sx={{ textAlign: 'center', width: '100%' }}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <RechartsPieChart width={120} height={120}>
                      <Pie
                        data={[
                          { name: 'Achieved', value: kpiData.financial.profit_margin, fill: '#4CAF50' },
                          { name: 'Remaining', value: Math.max(0, 20 - kpiData.financial.profit_margin), fill: '#E0E0E0' }
                        ]}
                        cx={60}
                        cy={60}
                        innerRadius={35}
                        outerRadius={50}
                        startAngle={90}
                        endAngle={450}
                        dataKey="value"
                      />
                    </RechartsPieChart>
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                        {kpiData.financial.profit_margin}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Net Profit Margin %
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        Target 12.0%
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Ratio */}
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PieChart color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Quick Ratio</Typography>
                  </Box>
                  <Typography variant="h4" color="primary.main">
                    1.02
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    1 or higher
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    Quick Ratio Target
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Additional KPIs Row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Inventory color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Total Purchases</Typography>
                  </Box>
                  <Typography variant="h4" color="secondary.main">
                    {formatCurrency(kpiData.purchase.total_amount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatNumber(kpiData.purchase.total_bills)} purchase bills
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <People color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6">Customers</Typography>
                  </Box>
                  <Typography variant="h4" color="warning.main">
                    {formatNumber(kpiData.sales.unique_customers)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    +{kpiData.customers.new_customers} new
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Business color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Distributors</Typography>
                  </Box>
                  <Typography variant="h4" color="primary.main">
                    {formatNumber(kpiData.purchase.unique_distributors)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active partners
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocalPharmacy color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6">Medicines</Typography>
                  </Box>
                  <Typography variant="h4" color="info.main">
                    {formatNumber(kpiData.sales.unique_medicines)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique items sold
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Cash Flow Diagram */}
          <CashFlowDiagram 
            storeId={selectedStore}
            selectedStore={selectedStore === 'all' ? null : stores.find(s => s.id.toString() === selectedStore) || null}
            dateRange={dateRange}
            totals={{
              totalSales: kpiData?.sales.total_sales || 0,
              totalPurchase: kpiData?.purchase.total_amount || 0
            }}
          />

          {/* Income and Expenses Chart - Combined Bar and Line Chart */}
          {trendsData.length > 0 && purchaseTrendsData.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }} onClick={() => toggleSection('trends')}>
                <Assessment sx={{ mr: 1 }} />
                <Typography variant="h6">Income and Expenses</Typography>
                <IconButton>
                  {expandedSections.trends ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedSections.trends}>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={trendsData.map((salesItem: any, index: number) => {
                      const purchaseItem = purchaseTrendsData[index] || {};
                      return {
                        ...salesItem,
                        total_purchase_amount: purchaseItem.total_purchase_amount || 0,
                        net_profit: (salesItem.total_sales || 0) - (purchaseItem.total_purchase_amount || 0)
                      };
                    })}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="period" 
                        tickFormatter={(value: any) => {
                          const date = new Date(value);
                          return date.toLocaleDateString('en-GB');
                        }}
                      />
                      <YAxis domain={[-5000, 6000]} />
                      <Tooltip 
                        formatter={(value: any, name: any) => [formatCurrency(Number(value)), name]}
                        labelFormatter={(label: any) => {
                          const date = new Date(label);
                          return date.toLocaleDateString('en-GB');
                        }}
                      />
                      <Bar dataKey="total_sales" fill="#1976d2" name="Total Income" />
                      <Bar dataKey="total_purchase_amount" fill="#42a5f5" name="Total Expenses" />
                      <Line type="monotone" dataKey="net_profit" stroke="#4CAF50" strokeWidth={2} name="Net" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Collapse>
            </Paper>
          )}

          {/* Budget Tracking Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Income Budget */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">% of Income Budget</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <RechartsPieChart width={150} height={150}>
                      <Pie
                        data={[
                          { name: 'Used', value: 94, fill: '#1976d2' },
                          { name: 'Remaining', value: 6, fill: '#E0E0E0' }
                        ]}
                        cx={75}
                        cy={75}
                        innerRadius={50}
                        outerRadius={70}
                        startAngle={90}
                        endAngle={450}
                        dataKey="value"
                      />
                    </RechartsPieChart>
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        94%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Budget ₹5,000.00
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Balance -₹281.00
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Expenses Budget */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CreditCard color="error" sx={{ mr: 1 }} />
                  <Typography variant="h6">% of Expenses Budget</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <RechartsPieChart width={150} height={150}>
                      <Pie
                        data={[
                          { name: 'Used', value: 93, fill: '#d32f2f' },
                          { name: 'Remaining', value: 7, fill: '#E0E0E0' }
                        ]}
                        cx={75}
                        cy={75}
                        innerRadius={50}
                        outerRadius={70}
                        startAngle={90}
                        endAngle={450}
                        dataKey="value"
                      />
                    </RechartsPieChart>
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                        93%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Budget ₹3,500.00
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Balance -₹230.00
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Staff Performance */}
          {staffData.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }} onClick={() => toggleSection('staff')}>
                <People sx={{ mr: 1 }} />
                <Typography variant="h6">Staff Performance</Typography>
                <IconButton>
                  {expandedSections.staff ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedSections.staff}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Staff Name</TableCell>
                        <TableCell align="right">Total Sales</TableCell>
                        <TableCell align="right">Bills</TableCell>
                        <TableCell align="right">Customers</TableCell>
                        <TableCell align="right">Avg Bill Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {staffData.slice(0, 10).map((staff: any) => (
                        <TableRow key={staff.staff_name}>
                          <TableCell>{staff.staff_name}</TableCell>
                          <TableCell align="right">{formatCurrency(staff.total_sales)}</TableCell>
                          <TableCell align="right">{formatNumber(staff.total_bills)}</TableCell>
                          <TableCell align="right">{formatNumber(staff.unique_customers_served)}</TableCell>
                          <TableCell align="right">{formatCurrency(staff.avg_bill_value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Paper>
          )}

          {/* Top Distributors */}
          {distributorData.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }} onClick={() => toggleSection('distributors')}>
                <Business sx={{ mr: 1 }} />
                <Typography variant="h6">Top Distributors</Typography>
                <IconButton>
                  {expandedSections.distributors ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedSections.distributors}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Distributor</TableCell>
                        <TableCell align="right">Invoice Amount</TableCell>
                        <TableCell align="right">Bills</TableCell>
                        <TableCell align="right">Discount</TableCell>
                        <TableCell align="right">Discount %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {distributorData.map((distributor: any) => (
                        <TableRow key={distributor.distributor_name}>
                          <TableCell>{distributor.distributor_name}</TableCell>
                          <TableCell align="right">{formatCurrency(distributor.total_invoice_amount)}</TableCell>
                          <TableCell align="right">{formatNumber(distributor.total_bills)}</TableCell>
                          <TableCell align="right">{formatCurrency(distributor.total_discount)}</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={`${distributor.discount_percentage}%`} 
                              color={distributor.discount_percentage > 10 ? "error" : "success"}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Paper>
          )}
        </>
      )}
    </Container>
  );
};

export default FinanceDashboard;
